/**
 * 解析ドック（§4 / Phase 2）。検索・制限・ORF・プライマー・CRISPR・情報。
 */

import { useMemo, useState } from 'react';
import { Button, ButtonGroup, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  CRISPR_NUCLEASES,
  RESTRICTION_LIBRARY,
  STARTER_ENZYME_IDS,
  formatIntervalRange1Based,
  type GenomicFeatureV1,
  type Interval,
} from '@kensuke/genome-core';
import { useAuth } from '../../../hooks/useAuth';
import {
  listWorkspaceCatalog,
  syncWorkspaceCatalog,
  type WorkspaceCatalogItem,
} from '../../../services/geneAnalysisWorkspaceCatalog.service';
import {
  SEARCH_EXAMPLE_PATTERNS,
  visibleAnalysisTabs,
  type AnalysisTab,
} from './geneAnalysisAnalysis';
import type { GeneAnalysisAnalysisState } from './useGeneAnalysisAnalysis';
import type { StoredSequenceMeta } from '../../../services/geneAnalysisChunkStore.service';

export interface GeneAnalysisAnalysisPanelProps {
  tab: AnalysisTab;
  onTabChange: (tab: AnalysisTab) => void;
  meta: StoredSequenceMeta;
  analysis: GeneAnalysisAnalysisState;
  features: GenomicFeatureV1[];
  onJump: (center: number, span?: number) => void;
  onSelect: (interval: Interval) => void;
  onAddFeature: (feature: Omit<GenomicFeatureV1, 'id'>) => void;
  onDeleteFeature: (id: string) => void;
  onRename: (name: string) => void;
  onTopologyChange: (topology: 'linear' | 'circular') => void;
  onExport: (kind: 'gproj' | 'fasta' | 'genbank') => void;
  selection: Interval | null;
}

export default function GeneAnalysisAnalysisPanel({
  tab,
  onTabChange,
  meta,
  analysis,
  features,
  onJump,
  onSelect,
  onAddFeature,
  onDeleteFeature,
  onRename,
  onTopologyChange,
  onExport,
  selection,
}: GeneAnalysisAnalysisPanelProps) {
  const { t } = useTranslation();
  const tabs = visibleAnalysisTabs(meta.length, meta.topology);
  const windowLabel = analysis.windowInfo
    ? `${t(`geneAnalysis.scan.${analysis.windowInfo.reason}`)} · ${formatIntervalRange1Based(
        analysis.windowInfo.interval,
        meta.length
      )}`
    : '';

  return (
    <aside className="ga-analysis-panel" data-testid="gene-analysis-analysis-panel">
      <div className="ga-analysis-tabs" role="tablist">
        {tabs.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'is-active' : ''}
            data-testid={`gene-analysis-tab-${item}`}
            onClick={() => onTabChange(item)}
          >
            {t(`geneAnalysis.tabs.${item}`)}
          </button>
        ))}
      </div>
      {windowLabel && (
        <p className="ga-analysis-window" data-testid="gene-analysis-scan-window">
          {windowLabel}
          {analysis.windowInfo?.truncated ? ` · ${t('geneAnalysis.scan.truncated')}` : ''}
        </p>
      )}
      {analysis.error && <p className="ga-analysis-error">{analysis.error}</p>}
      {tab === 'search' && (
        <SearchPane analysis={analysis} meta={meta} onJump={onJump} onSelect={onSelect} />
      )}
      {tab === 'restriction' && (
        <RestrictionPane analysis={analysis} meta={meta} onJump={onJump} onSelect={onSelect} />
      )}
      {tab === 'primer' && <PrimerPane analysis={analysis} />}
      {tab === 'crispr' && <CrisprPane analysis={analysis} onJump={onJump} onSelect={onSelect} />}
      {(tab === 'plasmid' || tab === 'info') && (
        <InfoPane
          meta={meta}
          analysis={analysis}
          features={features}
          onJump={onJump}
          onSelect={onSelect}
          onAddFeature={onAddFeature}
          onDeleteFeature={onDeleteFeature}
          onRename={onRename}
          onTopologyChange={onTopologyChange}
          onExport={onExport}
          selection={selection}
        />
      )}
    </aside>
  );
}

function SearchPane({
  analysis,
  meta,
  onJump,
  onSelect,
}: {
  analysis: GeneAnalysisAnalysisState;
  meta: StoredSequenceMeta;
  onJump: (center: number, span?: number) => void;
  onSelect: (interval: Interval) => void;
}) {
  const { t } = useTranslation();
  const hits = analysis.result.hits.slice(0, analysis.hitLimit);
  return (
    <div data-testid="gene-analysis-search-pane">
      <Form.Control
        size="sm"
        value={analysis.query}
        onChange={(event) => analysis.setQuery(event.target.value)}
        placeholder={t('geneAnalysis.search.placeholder')}
        data-testid="gene-analysis-search-query"
      />
      <div className="ga-analysis-row">
        <Form.Check
          type="switch"
          id="ga-search-both"
          label={t('geneAnalysis.search.both')}
          checked={analysis.bothStrands}
          onChange={(event) => analysis.setBothStrands(event.target.checked)}
        />
        {meta.length > 100_000 && (
          <Button size="sm" variant="primary" onClick={() => void analysis.run(true)}>
            {t('geneAnalysis.search.run')}
          </Button>
        )}
      </div>
      <div className="ga-chip-row" data-testid="gene-analysis-search-assist">
        {SEARCH_EXAMPLE_PATTERNS.map((pattern) => (
          <Button
            key={pattern}
            size="sm"
            variant={analysis.query === pattern ? 'secondary' : 'outline-secondary'}
            aria-current={analysis.query === pattern}
            onClick={() => analysis.setQuery(pattern)}
          >
            {pattern}
          </Button>
        ))}
      </div>
      <p className="ga-analysis-count">
        {analysis.running
          ? t('geneAnalysis.common.running')
          : t('geneAnalysis.search.count', { count: analysis.result.hits.length })}
      </p>
      <ul className="ga-hit-list">
        {hits.map((hit, index) => (
          <li key={`${hit.start}-${hit.strand}-${index}`}>
            <button
              type="button"
              className={index === analysis.activeIndex ? 'is-active' : ''}
              onClick={() => {
                analysis.setActiveIndex(index);
                onSelect({ start: hit.start, end: hit.end });
                onJump((hit.start + hit.end) / 2, Math.max(80, hit.end - hit.start));
              }}
            >
              {formatIntervalRange1Based(hit, meta.length)} · {hit.strand === 1 ? '+' : '−'}
            </button>
          </li>
        ))}
      </ul>
      {analysis.result.hits.length > analysis.hitLimit && (
        <Button size="sm" variant="link" onClick={() => analysis.setHitLimit((value) => value + 80)}>
          {t('geneAnalysis.search.more')}
        </Button>
      )}
    </div>
  );
}

function RestrictionPane({
  analysis,
  meta,
  onJump,
  onSelect,
}: {
  analysis: GeneAnalysisAnalysisState;
  meta: StoredSequenceMeta;
  onJump: (center: number, span?: number) => void;
  onSelect: (interval: Interval) => void;
}) {
  const { t } = useTranslation();
  const [customName, setCustomName] = useState('');
  const [customPattern, setCustomPattern] = useState('');
  const visible = useMemo(
    () => [
      ...RESTRICTION_LIBRARY.filter((enzyme) => enzyme.starter || analysis.enzymeIds.includes(enzyme.id)),
      ...analysis.customEnzymes,
    ],
    [analysis.customEnzymes, analysis.enzymeIds]
  );

  return (
    <div data-testid="gene-analysis-restriction-pane">
      <div className="ga-analysis-row">
        <Button size="sm" variant="outline-secondary" onClick={() => analysis.setEnzymeIds([...STARTER_ENZYME_IDS])}>
          {t('geneAnalysis.restriction.starters')}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => analysis.setEnzymeIds(RESTRICTION_LIBRARY.map((enzyme) => enzyme.id))}
        >
          {t('geneAnalysis.restriction.all')}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={() => analysis.setEnzymeIds([])}>
          {t('geneAnalysis.restriction.clear')}
        </Button>
      </div>
      <div className="ga-enzyme-list" data-testid="gene-analysis-restriction-enzyme-picker">
        {visible.map((enzyme) => (
          <Form.Check
            key={enzyme.id}
            type="checkbox"
            id={`ga-enz-${enzyme.id}`}
            label={`${enzyme.name} ${enzyme.pattern}`}
            checked={analysis.enzymeIds.includes(enzyme.id)}
            onChange={(event) => {
              analysis.setEnzymeIds((previous) =>
                event.target.checked
                  ? [...previous, enzyme.id]
                  : previous.filter((id) => id !== enzyme.id)
              );
            }}
          />
        ))}
      </div>
      <div className="ga-analysis-row">
        <Form.Control
          size="sm"
          placeholder={t('geneAnalysis.restriction.customName')}
          value={customName}
          onChange={(event) => setCustomName(event.target.value)}
        />
        <Form.Control
          size="sm"
          placeholder="GAATTC"
          value={customPattern}
          onChange={(event) => setCustomPattern(event.target.value)}
        />
        <Button
          size="sm"
          onClick={() => {
            if (analysis.addCustomEnzyme(customName, customPattern, 1, 4)) {
              setCustomName('');
              setCustomPattern('');
            }
          }}
        >
          {t('geneAnalysis.restriction.addCustom')}
        </Button>
      </div>
      <p className="ga-analysis-count">
        {t('geneAnalysis.restriction.cuts', { count: analysis.result.cuts.length })}
      </p>
      <ul className="ga-hit-list">
        {analysis.result.cuts.slice(0, 80).map((cut, index) => (
          <li key={`${cut.enzymeId}-${cut.cut}-${index}`}>
            <button
              type="button"
              className={index === analysis.activeIndex ? 'is-active' : ''}
              onClick={() => {
                analysis.setActiveIndex(index);
                onSelect({ start: cut.start, end: cut.end });
                onJump(cut.cut, 120);
              }}
            >
              {cut.enzymeName} · {cut.cut + 1} · {cut.overhang}
              {cut.unique ? ` · ${t('geneAnalysis.restriction.unique')}` : ''}
            </button>
          </li>
        ))}
      </ul>
      {analysis.result.fragments.length > 0 && (
        <details>
          <summary>{t('geneAnalysis.restriction.fragments', { count: analysis.result.fragments.length })}</summary>
          <ul className="ga-hit-list">
            {analysis.result.fragments.slice(0, 24).map((fragment, index) => (
              <li key={`${fragment.start}-${index}`}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect({ start: fragment.start, end: fragment.end });
                    onJump((fragment.start + Math.min(fragment.end, meta.length)) / 2, fragment.length);
                  }}
                >
                  {formatIntervalRange1Based(fragment, meta.length)} · {fragment.length.toLocaleString()} bp
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function PrimerPane({ analysis }: { analysis: GeneAnalysisAnalysisState }) {
  const { t } = useTranslation();
  const { forward, reverse } = analysis.result.primer;
  const copy = (text: string) => void navigator.clipboard.writeText(text);
  return (
    <div data-testid="gene-analysis-primer-pane">
      <div className="ga-analysis-row">
        <Form.Label>{t('geneAnalysis.primer.targetTm')}</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          min={40}
          max={80}
          value={analysis.primerSettings.targetTm}
          onChange={(event) =>
            analysis.setPrimerSettings((previous) => ({
              ...previous,
              targetTm: Number(event.target.value),
            }))
          }
        />
      </div>
      {forward && (
        <p>
          F {forward.tm.toFixed(1)}°C · {forward.sequence}
          <Button size="sm" variant="link" onClick={() => copy(forward.sequence)}>
            {t('geneAnalysis.selection.copy')}
          </Button>
        </p>
      )}
      {reverse && (
        <p>
          R {reverse.tm.toFixed(1)}°C · {reverse.sequence}
          <Button size="sm" variant="link" onClick={() => copy(reverse.sequence)}>
            {t('geneAnalysis.selection.copy')}
          </Button>
        </p>
      )}
      {forward && reverse && (
        <Button size="sm" onClick={() => copy(`${forward.sequence}\n${reverse.sequence}`)}>
          {t('geneAnalysis.primer.copyBoth')}
        </Button>
      )}
      {!forward && <p className="ga-analysis-muted">{t('geneAnalysis.primer.empty')}</p>}
    </div>
  );
}

function CrisprPane({
  analysis,
  onJump,
  onSelect,
}: {
  analysis: GeneAnalysisAnalysisState;
  onJump: (center: number, span?: number) => void;
  onSelect: (interval: Interval) => void;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="gene-analysis-crispr-pane">
      <Form.Select
        size="sm"
        value={analysis.nucleaseId}
        onChange={(event) => analysis.setNucleaseId(event.target.value)}
      >
        {CRISPR_NUCLEASES.map((nuclease) => (
          <option key={nuclease.id} value={nuclease.id}>
            {nuclease.name} · {nuclease.pam}
          </option>
        ))}
      </Form.Select>
      <Form.Control
        className="mt-2"
        size="sm"
        placeholder={t('geneAnalysis.crispr.customPam')}
        value={analysis.customPam}
        onChange={(event) => analysis.setCustomPam(event.target.value)}
      />
      <p className="ga-analysis-count">
        {t('geneAnalysis.crispr.count', { count: analysis.result.guides.length })}
      </p>
      <ul className="ga-hit-list">
        {analysis.result.guides.map((guide, index) => (
          <li key={`${guide.start}-${guide.strand}`}>
            <button
              type="button"
              className={index === analysis.activeIndex ? 'is-active' : ''}
              onClick={() => {
                analysis.setActiveIndex(index);
                onSelect({ start: guide.start, end: guide.end });
                onJump((guide.start + guide.end) / 2, 80);
              }}
            >
              {guide.spacer} · {guide.pam} · {guide.score}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InfoPane({
  meta,
  analysis,
  features,
  onJump,
  onSelect,
  onAddFeature,
  onDeleteFeature,
  onRename,
  onTopologyChange,
  onExport,
  selection,
}: {
  meta: StoredSequenceMeta;
  analysis: GeneAnalysisAnalysisState;
  features: GenomicFeatureV1[];
  onJump: (center: number, span?: number) => void;
  onSelect: (interval: Interval) => void;
  onAddFeature: (feature: Omit<GenomicFeatureV1, 'id'>) => void;
  onDeleteFeature: (id: string) => void;
  onRename: (name: string) => void;
  onTopologyChange: (topology: 'linear' | 'circular') => void;
  onExport: (kind: 'gproj' | 'fasta' | 'genbank') => void;
  selection: Interval | null;
}) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [name, setName] = useState(meta.name);
  const [catalog, setCatalog] = useState<WorkspaceCatalogItem[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const orfs = analysis.predictedFeatures;

  return (
    <div data-testid="gene-analysis-info-pane">
      <Form.Control size="sm" value={name} onChange={(event) => setName(event.target.value)} />
      <Button size="sm" className="mt-1" onClick={() => onRename(name.trim() || meta.name)}>
        {t('geneAnalysis.info.rename')}
      </Button>
      <ButtonGroup size="sm" className="mt-2 d-block">
        <Button
          variant={meta.topology === 'linear' ? 'secondary' : 'outline-secondary'}
          onClick={() => onTopologyChange('linear')}
        >
          {t('geneAnalysis.status.linear')}
        </Button>
        <Button
          variant={meta.topology === 'circular' ? 'secondary' : 'outline-secondary'}
          onClick={() => onTopologyChange('circular')}
        >
          {t('geneAnalysis.status.circular')}
        </Button>
      </ButtonGroup>
      <div className="ga-analysis-row mt-2">
        <Button size="sm" onClick={() => onExport('gproj')}>
          {t('geneAnalysis.export.gproj')}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={() => onExport('fasta')}>
          FASTA
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={() => onExport('genbank')}>
          GenBank
        </Button>
      </div>
      {selection && selection.end - selection.start >= 2 && (
        <Button
          size="sm"
          className="mt-2"
          data-testid="gene-analysis-add-feature"
          onClick={() =>
            onAddFeature({
              type: 'misc_feature',
              name: `sel-${selection.start + 1}`,
              start: selection.start,
              end: selection.end,
              strand: 1,
            })
          }
        >
          {t('geneAnalysis.info.addFeature')}
        </Button>
      )}
      <p className="ga-analysis-count mt-2">{t('geneAnalysis.info.features', { count: features.length })}</p>
      <ul className="ga-hit-list">
        {features.slice(0, 60).map((feature) => (
          <li key={feature.id}>
            <button
              type="button"
              onClick={() => {
                onSelect({ start: feature.start, end: feature.end });
                onJump((feature.start + feature.end) / 2, Math.max(80, feature.end - feature.start));
              }}
            >
              {feature.type} · {feature.name}
            </button>
            <Button size="sm" variant="link" onClick={() => onDeleteFeature(feature.id)}>
              {t('geneAnalysis.info.delete')}
            </Button>
          </li>
        ))}
      </ul>
      {orfs.length > 0 && (
        <details open>
          <summary>{t('geneAnalysis.info.orfs', { count: orfs.length })}</summary>
          <ul className="ga-hit-list">
            {orfs.slice(0, 40).map((feature) => (
              <li key={feature.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect({ start: feature.start, end: feature.end });
                    onJump((feature.start + feature.end) / 2, Math.max(80, feature.end - feature.start));
                  }}
                >
                  {feature.name}
                </button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() =>
                    onAddFeature({
                      type: 'CDS',
                      name: feature.name,
                      start: feature.start,
                      end: feature.end,
                      strand: feature.strand,
                    })
                  }
                >
                  {t('geneAnalysis.info.keepOrf')}
                </Button>
              </li>
            ))}
          </ul>
        </details>
      )}
      <details className="mt-2" data-testid="gene-analysis-catalog">
        <summary>{t('geneAnalysis.catalog.title')}</summary>
        {!isAuthenticated ? (
          <p className="ga-analysis-muted">{t('geneAnalysis.catalog.login')}</p>
        ) : (
          <>
            <Button
              size="sm"
              onClick={() => {
                void syncWorkspaceCatalog({
                  clientKey: meta.projectId,
                  projectName: meta.name,
                  sequenceCount: 1,
                  totalBp: meta.length,
                  fileName: `${meta.name}.gproj`,
                })
                  .then(() => listWorkspaceCatalog())
                  .then(setCatalog)
                  .catch((error: unknown) =>
                    setCatalogError(error instanceof Error ? error.message : String(error))
                  );
              }}
            >
              {t('geneAnalysis.catalog.sync')}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              className="ms-1"
              onClick={() => {
                void listWorkspaceCatalog()
                  .then(setCatalog)
                  .catch((error: unknown) =>
                    setCatalogError(error instanceof Error ? error.message : String(error))
                  );
              }}
            >
              {t('geneAnalysis.catalog.list')}
            </Button>
            {catalogError && <p className="ga-analysis-error">{catalogError}</p>}
            <ul className="ga-hit-list">
              {catalog.slice(0, 8).map((item) => (
                <li key={item.clientKey}>
                  {item.projectName} · {item.totalBp.toLocaleString()} bp
                </li>
              ))}
            </ul>
          </>
        )}
      </details>
    </div>
  );
}
